import React, { useState } from "react";
import { useApp } from "../../context/AppContext";
import { traitLevelColor } from "../../utils/scoring";
import PageShell from "../../components/layout/PageShell";
import GlassCard from "../../components/ui/GlassCard";
import ProgressBar from "../../components/ui/ProgressBar";
import Badge from "../../components/ui/Badge";
import Button from "../../components/ui/Button";
import Modal from "../../components/ui/Modal";

const ReportsPage: React.FC = () => {
  const { results, exams, orgs, users, traits } = useApp();
  const [showReport, setShowReport] = useState<string | null>(null);

  const avg = results.length ? Math.round(results.reduce((a,r)=>a+r.percentage,0)/results.length) : 0;

  const REPORT_TYPES = [
    { id:"performance", title:"Exam Performance Report",    icon:"📊", desc:"Overall pass/fail and score distribution",    color:"#6366f1" },
    { id:"psychometric",title:"Psychometric Summary",       icon:"🧠", desc:"Big Five trait distribution across candidates",color:"#10b981" },
    { id:"candidate",   title:"Candidate Progress",         icon:"📈", desc:"Individual growth and attempt tracking",       color:"#f59e0b" },
    { id:"org",         title:"Organisation Overview",      icon:"🏢", desc:"Multi-tenant analytics and comparison",       color:"#ec4899" },
  ];

  return (
    <PageShell title="Reports" subtitle="Analytics and psychometric assessment reports">
      {/* Report cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {REPORT_TYPES.map(r => (
          <GlassCard key={r.id} hover className="p-5" onClick={() => setShowReport(r.id)}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl mb-4"
              style={{ background: `${r.color}18` }}>
              {r.icon}
            </div>
            <h3 className="font-extrabold text-gray-800 text-sm leading-snug">{r.title}</h3>
            <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{r.desc}</p>
            <Button size="sm" className="mt-4 w-full justify-center" variant="secondary">
              Generate Report
            </Button>
          </GlassCard>
        ))}
      </div>

      {/* Trait overview */}
      <GlassCard className="p-5">
        <h3 className="font-bold text-gray-800 mb-5">Trait Distribution — Latest Results</h3>
        {results[0]?.traitScores ? (
          <div className="space-y-4">
            {results[0].traitScores.map(ts => {
              const trait = traits.find(t => t.id === ts.traitId);
              return (
                <div key={ts.traitId} className="flex items-center gap-4">
                  <div className="w-44 flex items-center gap-2 flex-shrink-0">
                    <span className="text-sm font-semibold text-gray-700 truncate">{ts.traitName}</span>
                  </div>
                  <div className="flex-1">
                    <ProgressBar value={ts.score} max={ts.maxScore} color={"#6366f1"} height={12} />
                  </div>
                  <span className="text-sm font-extrabold text-gray-800 w-12 text-right">{ts.percentage}%</span>
                  <Badge text={ts.level} />
                </div>
              );
            })}
          </div>
        ) : <p className="text-gray-400 text-sm">No psychometric data available yet.</p>}
      </GlassCard>

      {/* Platform summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <GlassCard className="p-5">
          <h3 className="font-bold text-gray-800 mb-4">Organisations</h3>
          <div className="space-y-3">
            {orgs.map(org => (
              <div key={org.id} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl text-white text-xs font-bold flex items-center justify-center flex-shrink-0"
                  style={{ background: org.primaryColor }}>{org.name[0]}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">{org.name}</p>
                  <div className="mt-0.5">
                    <ProgressBar value={org.candidateCount} max={300} color={org.primaryColor} height={4} />
                  </div>
                </div>
                <span className="text-xs font-bold text-gray-600">{org.candidateCount}</span>
              </div>
            ))}
          </div>
        </GlassCard>

        <GlassCard className="p-5">
          <h3 className="font-bold text-gray-800 mb-4">Exam Type Distribution</h3>
          {(["aptitude","behavioral","iq","situational","descriptive"] as const).map(type => {
            const count = exams.filter(e=>e.type===type).length;
            return count > 0 ? (
              <div key={type} className="flex items-center gap-3 mb-3">
                <span className="text-xs font-semibold text-gray-600 w-24 capitalize">{type}</span>
                <div className="flex-1">
                  <ProgressBar value={count} max={exams.length||1} color="#6366f1" height={7} />
                </div>
                <span className="text-xs font-bold text-gray-600 w-4">{count}</span>
              </div>
            ) : null;
          })}
        </GlassCard>

        <GlassCard className="p-5">
          <h3 className="font-bold text-gray-800 mb-4">Score Summary</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm text-gray-600">Average Score</span>
                <span className="text-sm font-bold">{avg}%</span>
              </div>
              <ProgressBar value={avg} max={100} color="#6366f1" height={9} />
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm text-gray-600">Pass Rate (≥60%)</span>
                <span className="text-sm font-bold">{results.length ? Math.round(results.filter(r=>r.percentage>=60).length/results.length*100) : 0}%</span>
              </div>
              <ProgressBar value={results.filter(r=>r.percentage>=60).length} max={results.length||1} color="#10b981" height={9} />
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm text-gray-600">High Achievers (≥80%)</span>
                <span className="text-sm font-bold">{results.length ? Math.round(results.filter(r=>r.percentage>=80).length/results.length*100) : 0}%</span>
              </div>
              <ProgressBar value={results.filter(r=>r.percentage>=80).length} max={results.length||1} color="#f59e0b" height={9} />
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Report preview modal */}
      <Modal isOpen={!!showReport} onClose={() => setShowReport(null)} title="Report Preview" width="max-w-2xl">
        <div className="space-y-4">
          <div className="p-4 bg-indigo-50 rounded-2xl text-center">
            <p className="text-5xl mb-2">{REPORT_TYPES.find(r=>r.id===showReport)?.icon}</p>
            <h3 className="font-extrabold text-gray-800">{REPORT_TYPES.find(r=>r.id===showReport)?.title}</h3>
            <p className="text-sm text-gray-500 mt-1">Generated on {new Date().toLocaleDateString()}</p>
          </div>
          <p className="text-sm text-gray-600 text-center">
            In production this would render a full downloadable PDF/Excel report with charts. Connect to Firebase to enable real-time export.
          </p>
          <div className="flex gap-3 justify-center pt-2">
            <Button variant="secondary" onClick={() => setShowReport(null)}>Close</Button>
            <Button>📥 Export PDF</Button>
          </div>
        </div>
      </Modal>
    </PageShell>
  );
};

export default ReportsPage;
